/**
 * Helpers for the mcp_server remote-resource type.
 *
 * The backend namespaces every tool from a server as `mcp_{slug}__{tool}`,
 * so the slug is lowercase letters, digits and hyphens only (no underscores,
 * which would collide with the separator) and is immutable after creation.
 */

export const MCP_SERVER_TYPE = "mcp_server";

export const MCP_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,62}$/;

/**
 * How the backend talks to the server. `auto` tries streamable HTTP and falls
 * back to HTTP+SSE when the server refuses the streamable POST with 405; the
 * result is remembered, so `auto` costs one probe, not one per tool call.
 */
export type MCPTransport = "auto" | "streamable_http" | "sse";

export const MCP_TRANSPORT_OPTIONS: { value: MCPTransport; label: string }[] = [
  { value: "auto", label: "Auto (negotiate)" },
  { value: "streamable_http", label: "Streamable HTTP" },
  { value: "sse", label: "HTTP + SSE (legacy)" },
];

export const MCP_TIMEOUT_MIN = 5;
export const MCP_TIMEOUT_MAX = 900;

export interface MCPDetailsForm {
  slug: string;
  auth_header: string;
  auth_scheme: string;
  /** Empty string while the user has cleared the field; validated before send. */
  timeout_seconds: number | "";
  transport: MCPTransport;
}

export interface MCPDiscoveredTool {
  name: string;
  description?: string | null;
}

export interface MCPServerDetails extends Omit<
  MCPDetailsForm,
  "timeout_seconds"
> {
  timeout_seconds: number;
  toolkit_name: string;
  discovered_tools?: MCPDiscoveredTool[] | null;
}

export const getEmptyMCPForm = (): MCPDetailsForm => ({
  slug: "",
  auth_header: "Authorization",
  auth_scheme: "Bearer",
  timeout_seconds: 120,
  transport: "auto",
});

/** Derive a slug suggestion from a display name; may be empty for odd input. */
export const slugFromName = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);

export const isValidSlug = (slug: string): boolean =>
  MCP_SLUG_PATTERN.test(slug);

/** Mirrors the backend's `Field(ge=5, le=900)`; checked before send so an
 * out-of-range value never reaches the 422 path. */
export const isValidTimeout = (value: number | ""): boolean =>
  value !== "" &&
  Number.isInteger(value) &&
  value >= MCP_TIMEOUT_MIN &&
  value <= MCP_TIMEOUT_MAX;

/** Everything the create/update payload needs to be accepted server-side. */
export const isValidMCPForm = (
  form: MCPDetailsForm,
  { slugLocked = false }: { slugLocked?: boolean } = {},
): boolean =>
  (slugLocked || isValidSlug(form.slug)) &&
  isValidTimeout(form.timeout_seconds);

export const resourceTypeLabel = (resourceType: string): string => {
  switch (resourceType) {
    case "litellm_gateway":
      return "LiteLLM Gateway";
    case MCP_SERVER_TYPE:
      return "MCP Server";
    default:
      return "NucLearn Hub";
  }
};

/** What the create endpoint needs for `mcp`, or nothing for other types. */
export const buildCreateMCPPayload = (
  resourceType: string,
  form: MCPDetailsForm,
): { mcp?: MCPDetailsForm } => {
  if (resourceType !== MCP_SERVER_TYPE) return {};
  return {
    mcp: {
      slug: form.slug.trim(),
      auth_header: form.auth_header.trim() || "Authorization",
      auth_scheme: form.auth_scheme.trim(),
      timeout_seconds: Number(form.timeout_seconds),
      transport: form.transport,
    },
  };
};

/** The update endpoint refuses slug changes, so only the editable fields go. */
export const buildUpdateMCPPayload = (
  resourceType: string,
  form: MCPDetailsForm,
): { mcp?: Omit<MCPDetailsForm, "slug"> } => {
  if (resourceType !== MCP_SERVER_TYPE) return {};
  return {
    mcp: {
      auth_header: form.auth_header.trim() || "Authorization",
      auth_scheme: form.auth_scheme.trim(),
      timeout_seconds: Number(form.timeout_seconds),
      transport: form.transport,
    },
  };
};

/** Toolkit rows that come from MCP servers are named `MCP: {slug}` (the
 * backend's naming contract in `mre/ops/chat/tools/mcp`). */
export const MCP_TOOLKIT_PREFIX = "MCP: ";
export const isMcpToolkitName = (name: string): boolean =>
  name.startsWith(MCP_TOOLKIT_PREFIX);
